import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { ComicCard } from '@/components/base/ComicCard';
import { comics } from '@/mocks/comics';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === id);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="mt-4 text-xl text-foreground-900">漫画未找到</h1>
            <p className="mt-2 text-sm text-foreground-500">该漫画可能已下架或地址错误</p>
            <Link
              to="/"
              className="whitespace-nowrap mt-6 inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedComics = comics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 4);

  const mockChapters = Array.from({ length: Math.min(comic.chapters, 20) }, (_, i) => ({
    number: comic.chapters - i,
    title: `第${comic.chapters - i}话`,
    updateTime: new Date(Date.now() - i * 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  }));

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <div className="pt-1">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: comic.categoryName, path: `/category/${comic.category}` },
              { label: comic.title },
            ]}
          />
        </div>

        <section className="px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
              <div className="lg:w-[300px] flex-shrink-0">
                <div className="w-full max-w-[240px] mx-auto lg:mx-0 aspect-[3/4] rounded-lg overflow-hidden bg-background-100">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    title={`${comic.title} - 51漫画免费在线阅读`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="mt-6 space-y-3">
                  <Link
                    to={`/category/${comic.category}`}
                    className="whitespace-nowrap w-full flex items-center justify-center gap-2 py-3 rounded-full bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer"
                  >
                    <i className="ri-play-circle-line"></i> 开始阅读
                  </Link>
                  <button className="whitespace-nowrap w-full flex items-center justify-center gap-2 py-3 rounded-full border border-background-200/70 text-foreground-700 text-sm font-medium hover:bg-background-100 transition-colors cursor-pointer">
                    <i className="ri-heart-line"></i> 加入收藏
                  </button>
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 font-bold">
                  {comic.title}
                </h1>

                <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i> {comic.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-book-open-line"></i> {comic.categoryName}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className={`${comic.status === '连载中' ? 'ri-loader-4-line animate-spin' : 'ri-check-double-line'}`}></i>
                    {comic.status}
                  </span>
                  <span className="flex items-center gap-1 text-accent-500">
                    <i className="ri-star-fill"></i> {comic.rating} 分
                  </span>
                </div>

                <div className="mt-8 grid grid-cols-3 gap-4">
                  <div className="bg-background-100 rounded-lg p-4 text-center">
                    <p className="text-2xl md:text-3xl font-bold text-accent-500">{comic.chapters}</p>
                    <p className="mt-1 text-xs text-foreground-500">总章节</p>
                  </div>
                  <div className="bg-background-100 rounded-lg p-4 text-center">
                    <p className="text-2xl md:text-3xl font-bold text-accent-500">
                      {(comic.views / 10000).toFixed(0)}万
                    </p>
                    <p className="mt-1 text-xs text-foreground-500">累计阅读</p>
                  </div>
                  <div className="bg-background-100 rounded-lg p-4 text-center">
                    <p className="text-2xl md:text-3xl font-bold text-accent-500">{comic.rating}</p>
                    <p className="mt-1 text-xs text-foreground-500">评分 / 10</p>
                  </div>
                </div>

                <div className="mt-8">
                  <h2 className="text-lg font-medium text-foreground-900">作品简介</h2>
                  <p className="mt-3 text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                </div>

                <div className="mt-8">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-medium text-foreground-900">章节列表</h2>
                    <span className="text-sm text-foreground-400">共 {comic.chapters} 话</span>
                  </div>
                  <div className="border border-background-200/70 rounded-lg overflow-hidden">
                    <div className="divide-y divide-background-200/50 max-h-[400px] overflow-y-auto">
                      {mockChapters.slice(0, 10).map((ch, idx) => (
                        <a
                          key={ch.number}
                          href="#"
                          className="flex items-center justify-between px-4 py-3 hover:bg-background-100 transition-colors cursor-pointer group"
                        >
                          <span className="text-sm text-foreground-700 group-hover:text-accent-500 transition-colors">
                            {ch.title}
                          </span>
                          <span className="text-xs text-foreground-400">{ch.updateTime}</span>
                        </a>
                      ))}
                    </div>
                    {comic.chapters > 10 && (
                      <div className="px-4 py-3 bg-background-100 text-center">
                        <a href="#" className="text-sm text-accent-500 hover:text-accent-600 font-medium">
                          查看全部 {comic.chapters} 话 <i className="ri-arrow-down-s-line"></i>
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-1.5 h-5 rounded-full bg-accent-500"></span>
              <h2 className="text-lg font-medium text-foreground-900">用户评价</h2>
            </div>
            <div className="flex flex-col lg:flex-row gap-8 items-start">
              <div className="lg:w-[60%]">
                <blockquote className="text-xl md:text-2xl font-heading text-foreground-900 leading-relaxed">
                  &ldquo;这部漫画真的太精彩了！画风精美，剧情紧凑，每次更新都迫不及待要看。强烈推荐给喜欢{comic.categoryName}类漫画的朋友们。&rdquo;
                </blockquote>
                <div className="mt-4 flex items-center gap-3">
                  <span className="text-sm font-medium text-foreground-900">漫画爱好者 - 小明</span>
                  <span className="flex items-center gap-0.5 text-accent-500">
                    <i className="ri-star-fill text-sm"></i>
                    <i className="ri-star-fill text-sm"></i>
                    <i className="ri-star-fill text-sm"></i>
                    <i className="ri-star-fill text-sm"></i>
                    <i className="ri-star-fill text-sm"></i>
                  </span>
                  <span className="text-xs px-2 py-0.5 rounded bg-accent-100 text-accent-700">已验证读者</span>
                </div>
              </div>
              <div className="lg:w-[35%] flex justify-center">
                <div className="w-40 h-40 md:w-48 md:h-48 rounded-2xl overflow-hidden bg-background-200">
                  <img
                    src="https://readdy.ai/api/search-image?query=Abstract%20minimalist%20portrait%20illustration%20with%20warm%20tones%20orange%20and%20cream%2C%20simple%20clean%20design%2C%20modern%20digital%20art%2C%20soft%20gradient%20background&width=400&height=400&seq=review-avatar-generic&orientation=squarish"
                    alt="读者头像"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {relatedComics.length > 0 && (
          <section className="px-4 md:px-6 py-14 md:py-20">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-6 text-center">
                同类型推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} {...c} />
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="px-4 md:px-6 py-12 md:py-16">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 font-bold">
              准备开始阅读《{comic.title}》了吗？
            </h2>
            <p className="mt-3 text-sm text-foreground-500">
              目前已更新至第 {comic.chapters} 话，{comic.status === '连载中' ? '每周持续更新中' : '已完结可放心追读'}
            </p>
            <Link
              to={`/category/${comic.category}`}
              className="whitespace-nowrap mt-6 inline-flex items-center gap-2 px-8 py-3 rounded-full bg-primary-500 text-background-50 text-sm md:text-base font-medium hover:bg-primary-600 transition-colors cursor-pointer"
            >
              立即阅读 <i className="ri-arrow-right-line"></i>
            </Link>
            <div className="mt-10">
              <img
                src="https://readdy.ai/api/search-image?query=Abstract%20manga%20reading%20experience%20horizontal%20banner%2C%20warm%20orange%20and%20cream%20gradient%2C%20minimalist%20clean%20design%2C%20soft%20abstract%20shapes%2C%20modern%20digital%20art%2C%20no%20text&width=1200&height=250&seq=article-cta-generic&orientation=landscape"
                alt="开始阅读"
                className="w-full rounded-lg"
                loading="lazy"
              />
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: `${comic.title} - 51漫画免费在线阅读`,
            description: comic.description,
            author: {
              "@type": "Person",
              name: comic.author,
            },
            datePublished: comic.updateTime,
            dateModified: comic.updateTime,
            image: comic.cover,
            publisher: {
              "@type": "Organization",
              name: "51漫画",
              url: "https://www.51comic.com",
            },
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "首页",
                  item: "https://www.51comic.com",
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: comic.categoryName,
                  item: `https://www.51comic.com/category/${comic.category}`,
                },
                {
                  "@type": "ListItem",
                  position: 3,
                  name: comic.title,
                },
              ],
            },
          }),
        }}
      />
    </>
  );
}